<template>
  <header class="header-container">
    <div class="header-content">
      <!-- Logo -->
      <div class="logo">
        <img
          src="@/assets/head-foot/congzai-logo-top.svg"
          alt="聪崽AI"
          class="logo-image"
        />
      </div>

      <!-- 导航菜单 -->
      <nav class="nav-menu">
        <router-link
          to="/"
          class="nav-item"
          :class="{ active: isHomeActive }"
          active-class="active"
        >
          首页
        </router-link>
        <!-- 文章管理 - 仅合伙人可见 -->
        <router-link
          v-if="isPartnerLogin"
          to="/article-management"
          class="nav-item"
          :class="{ active: isArticleActive }"
          active-class="active"
        >
          文章管理
        </router-link>
        <!-- 视频课管理 - 仅合伙人可见 -->
        <router-link
        v-if="isPartnerLogin"
          :to="{ path: '/course-management', query: { media: 'video' } }"
          class="nav-item"
          :class="{ active: isCourseVideoActive }"
        >
          视频课管理
        </router-link>
        <!-- 音频课管理 - 仅合伙人可见 -->
        <router-link
        v-if="isPartnerLogin"
          :to="{ path: '/course-management', query: { media: 'audio' } }"
          class="nav-item"
          :class="{ active: isCourseAudioActive }"
        >
          音频课管理
        </router-link>
        <router-link
          v-if="isPartnerLogin"
          to="/operation-management"
          class="nav-item"
          :class="{ active: isOperationActive }"
        >
          运营管理
        </router-link>
        <!-- 我的课程 - 仅家长可见 -->
        <router-link
        v-if="isParentLogin"
          to="/my-ai-course"
          class="nav-item"
          :class="{ active: $route.path === '/my-ai-course' }"
        >
          我的课程
        </router-link>
        <!-- AI音频课 - 仅家长可见 -->
        <router-link
          v-if="isParentLogin"
          to="/audio-course-generator"
          class="nav-item"
          :class="{ active: isAudioCourseActive }"
          active-class="active"
        >
          AI音频课
        </router-link>

        <!-- <router-link
          to="/about-us"
          class="nav-item"
          :class="{ active: isAboutActive }"
          active-class="active"
        >
          关于我们
        </router-link> -->
      </nav>

      <!-- 用户信息 -->
      <div
        class="user-info"
        @mouseenter="showLogout = true"
        @mouseleave="showLogout = false"
      >
        <img :src="userAvatar" alt="头像" class="user-avatar" />
        <span class="user-name" :title="userName">{{
          truncatedUserName || "用户"
        }}</span>
        <img
          src="@/assets/head-foot/down-grey.svg"
          alt="下拉图标"
          class="dropdown-icon"
          :class="{ rotated: showLogout }"
        />
        <!-- 退出登录按钮 -->
        <div class="logout-container" :class="{ show: showLogout }">
          <button
            v-if="isPartnerLogin"
            class="logout-btn"
            @click="handleSwitchOrg"
          >
            切换机构
          </button>
          <button class="logout-btn" @click="handleLogout">退出登录</button>
        </div>
      </div>
    </div>
  </header>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useRoute } from "vue-router";
import { useRouter } from "vue-router";
import { useUserStore } from "@/stores/user";

const route = useRoute();
const router = useRouter();
const userStore = useUserStore();

// 响应式数据
const userName = ref("");
const userAvatar = ref("");
const showLogout = ref(false);

// 从userStore获取用户信息
const getUserInfoFromLocalStorage = () => {
  const username = userStore.userInfo?.username || userStore.username || "";
  const avatar = userStore.userInfo?.avatar || "";
  userName.value = username;
  userAvatar.value = avatar;
};

// 在组件挂载时获取用户信息
onMounted(() => {
  getUserInfoFromLocalStorage();
});

// 监听路由变化，确保用户信息正确
watch(
  () => route.path,
  () => {
    getUserInfoFromLocalStorage();
  },
);
// 计算用户类型
const loginRole = computed(() => {
  return userStore.userInfo?.role ?? "";
});

// 判断是否为家长登录
const isParentLogin = computed(() => {
  return Number(loginRole.value) === 2;
});

// 判断是否为合伙人登录
const isPartnerLogin = computed(() => {
  return Number(loginRole.value) === 1;
});
// 计算属性
const truncatedUserName = computed(() => {
  // 多余五个字显示省略号
  if (userName.value.length > 5) {
    return userName.value.substring(0, 5) + "...";
  }
  return userName.value;
});

const isHomeActive = computed(() => {
  // 判断当前路由是否与首页相关
  const currentPath = route.path;
  return currentPath === "/";
});

const isArticleActive = computed(() => {
  // 判断当前路由是否与文章管理相关
  const currentPath = route.path;
  return (
    currentPath === "/article-management" ||
    currentPath.startsWith("/create-article") // 使用路径前缀匹配动态路由
  );
});

const isCourseVideoActive = computed(() => {
  const currentPath = route.path;
  const media = route.query?.media;
  return (currentPath === "/course-management" && media !== "audio") || (currentPath === "/create-column" && media !== "audio") || (currentPath.startsWith("/course-detail/") && media !== "audio");
});
const isCourseAudioActive = computed(() => {
  const currentPath = route.path;
  const media = route.query?.media;
  return (currentPath === "/course-management" && media === "audio") || (currentPath === "/create-column" && media === "audio") || (currentPath.startsWith("/course-detail/") && media === "audio");
});
const isAudioCourseActive = computed(() => {
  // 判断当前路由是否与AI音频课相关
  const currentPath = route.path;
  return (
    currentPath === "/audio-course-generator" ||
    currentPath === "/audio-course-generate" ||
    currentPath === "/audio-course-history" ||
    currentPath === "/create-audio-course"
  );
});

const isOperationActive = computed(() => {
  // 判断当前路由是否与运营管理相关
  const currentPath = route.path;
  return currentPath === "/operation-management";
});
// const isAboutActive = computed(() => {
//   // 判断当前路由是否与关于我们相关
//   const currentPath = route.path;
//   return currentPath === "/about-us";
// });

// 处理退出登录
const handleLogout = () => {
  // 调用userStore的logout方法
  userStore.logout();
  // 跳转到登录页
  router.push("/login");
  // 关闭退出登录按钮
  showLogout.value = false;
};

const handleSwitchOrg = () => {
  showLogout.value = false;
  router.push({
    path: "/login",
    query: { mode: "org-select" },
  });
};
</script>

<style scoped>
.header-container {
  background-color: #ffffff;
  border-bottom: 1px solid #e0e0e0;
  padding: 12px 0;
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  box-sizing: border-box;
}

.logo {
  display: flex;
  align-items: center;
}

.logo-image {
  height: 36px;
  width: auto;
}

.nav-menu {
  display: flex;
  gap: 30px;
  margin-left: 40px;
  flex: 1;
}

.nav-item {
  text-decoration: none;
  color: #333333;
  font-size: 14px;
  font-weight: 500;
  transition: color 0.3s ease;
  padding: 4px 0;
  position: relative;
}

.nav-item:hover {
  color: #ff4d4f;
}

.nav-item.active {
  color: #ff4d4f;
  font-weight: bold;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.user-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: cover;
}

.user-name {
  font-size: 14px;
  color: #333333;
  max-width: 80px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dropdown-icon {
  width: 14px;
  height: 14px;
  cursor: pointer;
  transition: transform 0.3s ease;
}

.dropdown-icon:hover,
.dropdown-icon.rotated {
  transform: rotate(180deg);
}

/* 退出登录按钮样式 */
.user-info {
  position: relative;
}

.logout-container {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 8px;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  opacity: 0;
  visibility: hidden;
  transform: translateY(-10px);
  transition: all 0.3s ease;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  align-items: stretch;
}

.logout-container.show {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

.logout-btn {
  padding: 8px 16px;
  border: none;
  background: transparent;
  color: #333333;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  width: 100%;
  box-sizing: border-box;
  text-align: left;
}

.logout-btn:hover {
  background: #f5f5f5;
  color: #ff4d4f;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .header-content {
    padding: 0 10px;
  }

  .nav-menu {
    gap: 15px;
    margin-left: 20px;
  }

  .nav-item {
    font-size: 13px;
  }

  .user-name {
    max-width: 60px;
    font-size: 13px;
  }

  .user-avatar {
    width: 24px;
    height: 24px;
  }
}

@media (max-width: 576px) {
  .nav-menu {
    gap: 10px;
    margin-left: 10px;
  }

  .nav-item {
    font-size: 12px;
  }

  .user-name {
    display: none;
  }
}
</style>
